import moment from "moment";
import * as XLSX from "xlsx";
import { GridColDef } from "@mui/x-data-grid";

// ── Types ─────────────────────────────────────────────────────────────────────

export type FilterItem = {
  id?: string | number;
  field: string;
  operator: string;
  value: any;
};

export type Filters = {
  items: FilterItem[];
  quickFilterValues: any[];
};

export type PAType = "Spec" | "Sold";

export type PrintColumn<T> = {
  field: string;
  label: string;
  getValue: (row: T) => string;
};

// ── Filter summary lines (shown in print header and Excel header row) ─────────

export function buildFilterSummary(
  paType: PAType,
  stage: string,
  searchValue: string,
  selectedCommunities: string[],
  filt: Filters,
  totalRows: number,
  filteredCount: number
): string[] {
  const lines: string[] = [];
  lines.push(`Profit Analysis — ${paType} List`);
  lines.push(`Printed / Exported: ${moment().format("LLL")}`);

  const active: string[] = [];
  if (stage !== "All") active.push(`Stage: ${stage}`);
  if (searchValue) active.push(`Search: "${searchValue}"`);
  if (selectedCommunities.length > 0)
    active.push(`Communities: ${selectedCommunities.join(", ")}`);
  for (const item of filt.items) {
    if (item.field === "stageCode" || item.field === "subdivision") continue;
    const val = Array.isArray(item.value) ? item.value.join(", ") : item.value;
    active.push(`${item.field}: ${val}`);
  }
  if (filt.quickFilterValues.length > 0)
    active.push(`Quick filter: "${filt.quickFilterValues.join(" ")}"`);

  if (active.length > 0) {
    lines.push(`Filters: ${active.join(" | ")}`);
  } else {
    lines.push("Filters: None");
  }

  lines.push(`Showing ${filteredCount} of ${totalRows} row${totalRows !== 1 ? "s" : ""}`);
  return lines;
}

// ── Filename helper ───────────────────────────────────────────────────────────

export function buildFilename(paType: PAType, ext: "xlsx" | "pdf"): string {
  return `Profit-Analysis-${paType}-list-${moment().format("YYYY-MM-DD-HH-mm")}.${ext}`;
}

// ── Print with filename ───────────────────────────────────────────────────────

export function printWithFilename(paType: PAType): void {
  const previous = document.title;
  document.title = buildFilename(paType, "pdf");
  window.print();
  setTimeout(() => { document.title = previous; }, 500);
}

// ── Client-side filter (mirrors DataGrid operators) ───────────────────────────

const toCleanString = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

export function applyFilters<T extends object>(
  rows: T[],
  filt: Filters,
  columns: GridColDef[]
): T[] {
  let result = [...rows];

  for (const item of filt.items) {
    const col = columns.find((c) => c.field === item.field);
    result = result.filter((row) => {
      let cellValue: any;
      cellValue = (row as any)[item.field];
      if (cellValue === undefined && col?.valueGetter) {
        cellValue = (col.valueGetter as any)(undefined, row);
      }

      const cellStr = toCleanString(cellValue)?.toLowerCase() ?? "";

      if (item.operator === "isAnyOf" && Array.isArray(item.value)) {
        return item.value.some((v: string) => cellStr === v.toLowerCase());
      }
      if (item.operator === "equals") {
        return cellStr === String(item.value).toLowerCase();
      }
      if (item.operator === "contains") {
        return cellStr.includes(String(item.value).toLowerCase());
      }
      return true;
    });
  }

  if (filt.quickFilterValues.length > 0) {
    const terms = filt.quickFilterValues.map((v: any) => String(v).toLowerCase());
    result = result.filter((row) =>
      terms.every((term: string) =>
        Object.values(row).some((val) =>
          toCleanString(val)?.toLowerCase().includes(term)
        )
      )
    );
  }

  return result;
}

// ── Excel export ──────────────────────────────────────────────────────────────

export function exportToExcel<T extends object>(
  paType: PAType,
  rows: T[],
  columns: GridColDef[],
  filterSummaryLines: string[]
): void {
  const exportColumns = columns.filter((c) => c.field !== "profitAnalysisId");
  const headers = exportColumns.map((c) => c.headerName ?? c.field);

  const data = rows.map((row) =>
    exportColumns.map((col) => {
      let value: any;
      if (col.valueGetter) {
        value = (col.valueGetter as any)(undefined, row);
      } else {
        value = (row as any)[col.field];
      }
      if (col.valueFormatter) {
        value = (col.valueFormatter as any)(value, row);
      }

      // ── Stage: append day-count for stage 10 ─────────────────────────────
      if (col.field === "stageCode") {
        const stageDate = (row as any).stageDate;
        const code = String(value ?? "");
        if (code === "10" && stageDate) {
          const days = Math.floor(
            (Date.now() - new Date(stageDate).getTime()) / 86_400_000
          );
          value = `${code} [${days}d]`;
        }
      }

      return value ?? "";
    })
  );

  const summaryRows = filterSummaryLines.map((line) => [line]);
  const sheetData = [...summaryRows, [], headers, ...data];

  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  for (let r = 0; r < filterSummaryLines.length; r++) {
    const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[cellRef]) {
      ws[cellRef].s = { font: { bold: r === 0, italic: r > 0 }, fill: { fgColor: { rgb: "F3F4F6" } } };
    }
  }

  const colWidths = headers.map((h, i) => {
    const maxLen = Math.max(
      h.length,
      ...data.map((r) => String(r[i] ?? "").length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `${paType} List`);
  XLSX.writeFile(wb, buildFilename(paType, "xlsx"));
}

// ── Print portal management ───────────────────────────────────────────────────

export const PRINT_PORTAL_ID = "pa-list-print-portal";
export const SOLD_PRINT_PORTAL_ID = "sold-list-print-portal";
export const SPEC_PRINT_PORTAL_ID = "spec-list-print-portal";

export function getOrCreatePrintPortal(): HTMLElement {
  let el = document.getElementById(PRINT_PORTAL_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = PRINT_PORTAL_ID;
    document.body.appendChild(el);
  }
  return el;
}

// ── Shared currency formatter ─────────────────────────────────────────────────

export function formatUSD(value: any): string {
  if (!value) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
